import React, { createContext, useState, useEffect } from "react";
import { getUserData, saveUserData, removeUserData } from "../../../utils/authUtils";
import { supabase } from "../../../lib/supabaseClient";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check current Supabase session first
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          // Get user profile from database
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (profile) {
            const authData = {
              token: session.access_token,
              refreshToken: session.refresh_token,
              user: {
                id: profile.id,
                username: profile.username,
                email: profile.email,
                role: profile.role,
                isAdmin: profile.role === 'admin',
                isManager: profile.role === 'manager' || profile.role === 'uploader',
              },
            };
            setUserData(authData);
            saveUserData(authData);
          }
        } else {
          // Fallback to localStorage data
          const data = getUserData();
          if (data) {
            setUserData(data);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        const data = getUserData();
        if (data) {
          setUserData(data);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();

    // Listen for auth state changes (token refresh, sign in, sign out)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      
      if (event === 'TOKEN_REFRESHED' && session) {
        // Update stored tokens when refreshed
        const currentData = getUserData();
        if (currentData) {
          const updatedData = {
            ...currentData,
            token: session.access_token,
            refreshToken: session.refresh_token,
          };
          setUserData(updatedData);
          saveUserData(updatedData);
        }
      } else if (event === 'SIGNED_OUT') {
        setUserData(null);
        removeUserData();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Function to update user data and persist it
  const updateUserData = (data) => {
    setUserData(data);
    if (data) {
      saveUserData(data);
    } else {
      removeUserData();
    }
  };

  // Proper logout function that signs out from Supabase
  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUserData(null);
      removeUserData();
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if Supabase signOut fails
      setUserData(null);
      removeUserData();
    }
  };

  return (
    <UserContext.Provider value={{ userData, setUserData: updateUserData, isLoading, logout }}>
      {children}
    </UserContext.Provider>
  );
};
